import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { smsAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/numberFormat';

const ModernSidebar = ({ navigation, sidebarOpen, setSidebarOpen, user, logout }) => {
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Handle menu item click - close sidebar on mobile
  const handleMenuClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const isSelected = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  };

  const isSubmenuActive = (children) => {
    return children?.some(child => isSelected(child.href));
  };

  const toggleSubmenu = (menuKey) => {
    setOpenSubmenu(openSubmenu === menuKey ? null : menuKey);
  };

  // Auto-open submenu if any child is active
  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children && isSubmenuActive(item.children)) {
        setOpenSubmenu(item.menuKey);
      }
    });
  }, [location.pathname]);

  // Fetch balance with SMS capacity
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await smsAPI.getBalanceWithSMS();
        const balanceInfo = response.data?.data || response.data;
        setBalanceData(balanceInfo);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    };
    
    if (user?.role === 'user' || user?.role === 'USER') {
      fetchBalance();
    }
  }, [user]);

  // Click handler to refresh balance from Redis + DB
  const handleBalanceClick = async () => {
    if (balanceRefreshing) return;
    setBalanceRefreshing(true);
    try {
      const response = await smsAPI.refreshBalance();
      const balanceInfo = response.data?.data || response.data;
      setBalanceData(balanceInfo);
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    } finally {
      setBalanceRefreshing(false);
    }
  };

  const getIcon = (iconName, size = 16) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent style={{ width: `${size}px`, height: `${size}px` }} /> : <Icons.Circle style={{ width: `${size}px`, height: `${size}px` }} />;
  };

  return (
    <>
      {/* Sidebar Styles */}
      <style>{`
        .sidebar-nav::-webkit-scrollbar { width: 4px; }
        .sidebar-nav::-webkit-scrollbar-track { background: transparent; }
        .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.12); border-radius: 10px; }
        .sidebar-nav::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.2); }
        .sidebar-item { transition: all 0.15s ease; }
        .sidebar-item:active { transform: scale(0.98); }
        .submenu-enter { animation: submenuSlide 0.2s ease-out; }
        @keyframes submenuSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 bottom-0 z-40 transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: '62px',
          width: sidebarOpen && window.innerWidth < 1024 ? '280px' : '260px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(249,250,251,0.97) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: '1px solid rgba(99, 102, 241, 0.06)',
          boxShadow: '4px 0 24px rgba(99, 102, 241, 0.03)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Close button for mobile */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Navigation</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-150"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icons.X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav flex-1 px-2.5 py-2 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(99,102,241,0.12) transparent' }}>
            {navigation.map((item, navIndex) => {
              // Check if item has children (submenu)
              if (item.children) {
                const isOpen = openSubmenu === item.menuKey;
                const hasActiveChild = isSubmenuActive(item.children);
                const isHovered = hoveredItem === `parent-${item.menuKey}`;
                
                return (
                  <div key={item.name} className="mb-0.5">
                    {/* Parent menu item */}
                    <button
                      onClick={() => toggleSubmenu(item.menuKey)}
                      onMouseEnter={() => setHoveredItem(`parent-${item.menuKey}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className="sidebar-item w-full group relative flex items-center gap-2.5 px-2.5 rounded-lg font-medium"
                      style={{
                        height: '38px',
                        color: isOpen || hasActiveChild ? '#4338ca' : isHovered ? '#1e1b4b' : '#1e293b',
                        background: isOpen || hasActiveChild
                          ? 'rgba(99, 102, 241, 0.06)'
                          : isHovered ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
                      }}
                    >
                      {/* Active accent */}
                      {hasActiveChild && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2.5px] rounded-r-full" style={{
                          height: '18px',
                          background: 'linear-gradient(180deg, #6366f1 0%, #06b6d4 100%)',
                        }} />
                      )}
                      <div className="flex-shrink-0 w-[30px] h-[30px] rounded-lg flex items-center justify-center transition-all duration-150" style={{
                        background: isOpen || hasActiveChild
                          ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
                          : isHovered ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.04)',
                        color: isOpen || hasActiveChild ? '#fff' : isHovered ? '#4f46e5' : '#94a3b8',
                      }}>
                        {getIcon(item.icon, 15)}
                      </div>
                      <span className="text-[13px] truncate flex-1 text-left">{item.name}</span>
                      
                      <Icons.ChevronRight className="w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200" style={{
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        opacity: 0.4,
                      }} />
                    </button>

                    {/* Submenu items */}
                    <div style={{
                      maxHeight: isOpen ? `${item.children.length * 36 + 8}px` : '0px',
                      overflow: 'hidden',
                      transition: 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      <div className="py-1 ml-[18px] pl-2.5" style={{ borderLeft: '1.5px solid rgba(99, 102, 241, 0.1)' }}>
                        {item.children.map((child) => {
                          const childActive = isSelected(child.href);
                          const childHovered = hoveredItem === child.href;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={handleMenuClick}
                              onMouseEnter={() => setHoveredItem(child.href)}
                              onMouseLeave={() => setHoveredItem(null)}
                              className="sidebar-item submenu-enter group relative flex items-center gap-2.5 pl-3 pr-2.5 rounded-lg"
                              style={{
                                height: '34px',
                                color: childActive ? '#4338ca' : childHovered ? '#1e1b4b' : '#475569',
                                background: childActive
                                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(6, 182, 212, 0.04) 100%)'
                                  : childHovered ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
                                fontWeight: childActive ? 600 : 400,
                              }}
                            >
                              {/* Active dot on border */}
                              {childActive && (
                                <div className="absolute -left-[11.5px] top-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full" style={{
                                  background: 'linear-gradient(135deg, #6366f1, #06b6d4)',
                                  boxShadow: '0 0 6px rgba(99, 102, 241, 0.4)',
                                }} />
                              )}
                              
                              <div className="flex-shrink-0 transition-colors duration-150" style={{
                                color: childActive ? '#4f46e5' : childHovered ? '#6366f1' : '#94a3b8',
                              }}>
                                {getIcon(child.icon, 14)}
                              </div>
                              <span className="text-[12.5px] truncate">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              // Regular menu item (no children)
              const active = isSelected(item.href);
              const isHovered = hoveredItem === item.href;
              return (
                <div key={item.name} className="mb-0.5">
                  <Link
                    to={item.href}
                    onClick={handleMenuClick}
                    onMouseEnter={() => setHoveredItem(item.href)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className="sidebar-item group relative flex items-center gap-2.5 px-2.5 rounded-lg"
                    style={{
                      height: '38px',
                      color: active ? '#4338ca' : isHovered ? '#1e1b4b' : '#1e293b',
                      background: active
                        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(6, 182, 212, 0.04) 100%)'
                        : isHovered ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {/* Active accent */}
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2.5px] rounded-r-full" style={{
                        height: '18px',
                        background: 'linear-gradient(180deg, #6366f1 0%, #06b6d4 100%)',
                      }} />
                    )}
                    
                    <div className="flex-shrink-0 w-[30px] h-[30px] rounded-lg flex items-center justify-center transition-all duration-150" style={{
                      background: active
                        ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
                        : isHovered ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.04)',
                      color: active ? '#fff' : isHovered ? '#4f46e5' : '#94a3b8',
                    }}>
                      {getIcon(item.icon, 15)}
                    </div>
                    <span className="text-[13px] truncate flex-1">{item.name}</span>
                    
                    {item.badge && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold text-white rounded-md flex-shrink-0" style={{
                        background: 'linear-gradient(135deg, #ef4444, #f97316)',
                        lineHeight: '1',
                      }}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* User Profile Section */}
          <div className="p-2.5" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            {/* Balance card (for regular users) */}
            {(user?.balance !== undefined || balanceData?.available_balance !== undefined) && (
              <div
                className="mb-2 px-3 py-2.5 rounded-xl transition-all duration-150 cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(6, 182, 212, 0.04) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                }}
                onClick={handleBalanceClick}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.1)'; e.currentTarget.style.boxShadow = 'none'; }}
                title="Click to refresh balance"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icons.Wallet className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                    <span className="text-[11px] text-gray-500 font-medium">Balance</span>
                    <Icons.RefreshCw className={`w-2.5 h-2.5 text-gray-400 ${balanceRefreshing ? 'animate-spin' : ''}`} />
                  </div>
                  <span className="text-[13px] font-bold" style={{ color: '#059669' }}>
                    {balanceRefreshing ? '...' : formatIndianCurrency(balanceData?.available_balance || user.balance || 0)}
                  </span>
                </div>
                {balanceData?.sms_capacity && balanceData.sms_capacity > 0 && (
                  <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: '1px solid rgba(16, 185, 129, 0.08)' }}>
                    <span className="text-[10px] text-gray-400 font-medium">SMS Capacity</span>
                    <span className="text-[12px] font-bold" style={{ color: '#4f46e5' }}>
                      {balanceRefreshing ? '...' : balanceData.sms_capacity.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* User info + logout */}
            <div className="flex items-center gap-2.5 px-2 py-2">
              <div className="relative flex-shrink-0">
                <div className="rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{
                  width: '34px',
                  height: '34px',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                  boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)',
                }}>
                  {user?.full_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 border-[1.5px] border-white rounded-full" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-gray-800 truncate leading-tight">
                  {user?.full_name || user?.username}
                </p>
                <p className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">
                  {user?.role?.replace('_', ' ').toUpperCase()}
                </p>
              </div>
              <button
                onClick={logout}
                className="flex-shrink-0 w-[32px] h-[32px] rounded-lg flex items-center justify-center transition-all duration-150"
                style={{
                  color: '#94a3b8',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                title="Logout"
              >
                <Icons.LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModernSidebar;
